/**
 * Расчётный контекст карты (ADR-071 D1): «на какую площадь считаем деньги».
 *
 * Карта без варианта плана считается на 1 га (удельный режим) — это честно,
 * но абсолютные деньги, свод по месяцам и закупки появляются только когда
 * выбран вариант плана: площадь берётся из назначения (ручная либо авто по
 * строкам плана «культура × предшественник»).
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { useFarms } from '@/lib/reference/catalogScopes';
import {
  formatNumber,
  usePlanVariants,
  type TechCard,
} from '@/lib/planning/techCards';

export function CalcContextPicker({
  card,
  seasonId,
  scenarioId,
  onPick,
}: {
  card: TechCard;
  seasonId: string;
  /** Выбранный вариант плана; null — удельный режим (на 1 га). */
  scenarioId: string | null;
  onPick: (scenarioId: string | null) => void;
}) {
  const farms = useFarms();
  const [farmId, setFarmId] = useState<string | null>(card.farm_id);
  const plans = usePlanVariants(farmId, seasonId);
  const farmOptions = [...(farms.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Считаем на площадь:</span>
      <Pill active={scenarioId === null} onClick={() => onPick(null)}>
        1 га (без варианта)
      </Pill>
      {card.farm_id === null &&
        farmOptions.length > 1 &&
        farmOptions.map((farm) => (
          <Pill key={farm.id} active={farm.id === farmId} onClick={() => setFarmId(farm.id)} muted>
            {farm.name}
          </Pill>
        ))}
      {(plans.data ?? []).map((plan) => (
        <Pill key={plan.id} active={plan.id === scenarioId} onClick={() => onPick(plan.id)}>
          {plan.name}
          {plan.is_selected && (
            <Badge variant="primary" className="ml-1">
              план сезона
            </Badge>
          )}
        </Pill>
      ))}
      {farmId && !plans.isLoading && (plans.data ?? []).length === 0 && (
        <span className="text-xs text-muted-foreground">
          у хозяйства нет вариантов плана на сезон — деньги на площадь считать не из чего
        </span>
      )}
      {scenarioId && (
        <span className="text-xs text-muted-foreground">
          площадь — из назначения варианту: ручная, иначе авто по строкам плана
        </span>
      )}
    </div>
  );
}

function Pill({
  active,
  muted = false,
  onClick,
  children,
}: {
  active: boolean;
  muted?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
          : muted
            ? 'rounded-full border border-dashed border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
            : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
      }
    >
      {children}
    </button>
  );
}

/** Площадь и режим — подпись в шапке экономики. */
export function calcContextLabel(areaHa: number | null | undefined, basis: string | undefined): string {
  if (basis === 'assigned' && areaHa != null) return `на ${formatNumber(areaHa)} га по варианту плана`;
  return 'на 1 га — карта не привязана к варианту плана';
}
